/**
 * ChatWindowHeader Component - HYB Shop Chat
 * User info header with back button and actions
 */


import { ArrowLeft, RefreshCw } from 'lucide-react';
import { cn } from '../../../utils/responsive';
import { Avatar } from '../shared';
import { ConversationStatusBadge, ConversationStatus } from '../ConversationStatusBadge';

interface Conversation {
  conversationId: string;
  userId: string;
  userName?: string;
  phoneNumber?: string;
  isGuest?: boolean;
  guestId?: string;
  status?: string | ConversationStatus;
  createdAt?: string;
  assignedAdminId?: string;
  lastMessageSender?: 'user' | 'admin';
  lastMessagePreview?: string;
  lastMessageAt?: string;
  unreadCount?: number;
}

interface ChatWindowHeaderProps {
  conversation: Conversation;
  onBack?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isMobile?: boolean;
  className?: string;
}

export function ChatWindowHeader({
  conversation,
  onBack,
  onRefresh,
  isRefreshing = false,
  isMobile = false,
  className,
}: ChatWindowHeaderProps) {
  const displayName = conversation.isGuest
    ? `Guest #${conversation.guestId || 'ไม่ระบุ'}`
    : conversation.userName || 'ผู้ใช้';

  const subtitle = conversation.isGuest
    ? 'ผู้เยี่ยมชมไม่ลงทะเบียน'
    : conversation.phoneNumber || 'ไม่มีเบอร์โทรศัพท์';

  return (
    <div
      className={cn(
        'flex items-center justify-between',
        'border-b border-stone-200 dark:border-gray-700',
        'px-4 lg:px-6 py-3 lg:py-4',
        'bg-white dark:bg-gray-800',
        className
      )}
    >
      <div className="flex items-center gap-2 lg:gap-3 min-w-0">
        {/* Back Button - Mobile Only */}
        {isMobile && onBack && (
          <button
            onClick={onBack}
            className={cn(
              'flex items-center justify-center',
              'h-10 w-10 -ml-2',
              'rounded-xl',
              'text-stone-600 dark:text-gray-300',
              'transition-all duration-200',
              'hover:bg-stone-100 dark:hover:bg-gray-700',
              'active:scale-95',
              'focus:outline-none focus:ring-2 focus:ring-amber-400/50'
            )}
            aria-label="กลับไปรายการสนทนา"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}

        {/* Avatar */}
        <Avatar
          name={displayName}
          size="md"
          isGuest={conversation.isGuest}
          isOnline={!conversation.isGuest}
          className="flex-shrink-0"
        />

        {/* User Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-stone-900 dark:text-white truncate">
              {displayName}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-xs text-stone-500 dark:text-gray-400 truncate">
              {subtitle}
            </p>
            <ConversationStatusBadge
              status={(conversation.status || 'active') as ConversationStatus}
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className={cn(
            'flex items-center justify-center',
            'h-10 w-10',
            'rounded-xl border border-stone-200 dark:border-gray-700',
            'text-stone-500 dark:text-gray-400',
            'transition-all duration-200',
            'hover:bg-stone-50 dark:hover:bg-gray-700',
            'hover:border-amber-400 dark:hover:border-amber-500',
            'active:scale-95',
            'focus:outline-none focus:ring-2 focus:ring-amber-400/50',
            'disabled:opacity-60 disabled:cursor-not-allowed'
          )}
          title="รีเฟรชข้อความ"
          aria-label="รีเฟรชข้อความ"
        >
          <RefreshCw
            className={cn('h-4 w-4', isRefreshing && 'animate-spin')}
          />
        </button>
      )}
    </div>
  );
}

export default ChatWindowHeader;
